import { Flex, HStack, Text } from '@chakra-ui/react';
import { CopilotActions } from '@common/enums';
import {
  useCopilotAction,
  useCopilotChat,
  useCopilotReadable,
} from '@copilotkit/react-core';
import { useCopilotChatSuggestions } from '@copilotkit/react-ui';
import { Role, TextMessage } from '@copilotkit/runtime-client-gql';
import { useEffect, useMemo, useState } from 'react';
import { INDIGO_HELP_EMAIL } from '~/common/components/ContactUsLink';
import { useSaveRestoreMessages } from '~/common/copilot/hooks/useSaveRestoreMessages';
import { IndigoLogoSmall } from '~/common/icons';
import { useAppContext } from '../../../../../../services/main/contexts/AppContext';
import { useCreateBuyerOfferAnalysis } from '../../../BuyerOfferAnalysis/hooks/useCreateBuyerOfferAnalysis';
import { CopilotChat } from '../../../CopilotChat';
import { useListingPageContext } from '../ListingPageContext';

interface ListingChatProps {
  initialPrompt?: string;
  onClose: () => void;
}

export const ListingChat = ({ initialPrompt, onClose }: ListingChatProps) => {
  const {
    listing,
    propertyDetails,
    metrics,
    tables,
    confidence,
    isMyListing,
    isOffMarketListing,
  } = useListingPageContext();
  const { user } = useAppContext();
  const { appendMessage, isLoading } = useCopilotChat();
  const { handleCreate } = useCreateBuyerOfferAnalysis();

  const isUsersListing = isMyListing || isOffMarketListing;

  // Add action to append scorecard link when user asks about creating an offer
  useCopilotAction({
    name: CopilotActions.makeScoreCard,
    description: [
      'When user asks about creating or submitting an offer,',
      'append a message with a link to the scorecard.',
      'Keywords: create offer, make offer, send offer, submit offer',
    ].join(' '),
    parameters: [
      {
        name: 'listingId',
        type: 'string',
        description: 'ID of the listing',
        required: true,
      },
    ],
    available: listing?.id ? 'enabled' : 'disabled',
    handler: async ({ listingId }) => {
      const errorMessage =
        'Sorry, we are unable to provide a scorecard link at this time, please try again later.';

      if (!listingId) {
        return errorMessage;
      }

      const boa = await handleCreate({ listingId });
      if (!boa?.id) {
        return errorMessage;
      }

      const scorecardUrl = `${window.location.origin}/offer-scorecard/${boa.id}`;

      return `To help you create an offer, you can use our scorecard here: ${scorecardUrl}`;
    },
  });

  const initialMsgForBA = useMemo(() => {
    return `Hey ${user?.firstName}, your Listing Data Coach is ready 👋

  I've analyzed this listing against our database of successful purchase contracts in your market.
  
  What I can do for you:
  - Craft winning offer strategies based on actual accepted offers
  - Show you exactly what contract terms are working for winning agents
  - I do NOT have access to broader market data yet… coming soon!
  
  Ask me anything about this property or "What would make this offer win?"
  
  *Powered by Indigo's premium contract data*
  `;
  }, [user?.firstName]);

  const initialMsgForLA = useMemo(() => {
    return `Hey ${user?.firstName}, your Listing Coach is ready 👋

  I've analyzed your listing against our database of successful purchase contracts in your market.

  What I can do for you:
  - Figure out pricing strategy based on recent real-time accepted offers
  - Set expectations with your seller based off real-time market data

  Ask me anything about this listing or "What should we counter with?"

  *Powered by Indigo's premium contract data*
`;
  }, [user?.firstName]);

  const initialMessage = useMemo(() => {
    return [
      <SpecialSystemMessage
        key="listing-chat-initial-message"
        message="Welcome to your Listing Coach"
      />,
      isUsersListing ? initialMsgForLA : initialMsgForBA,
    ];
  }, [isUsersListing, initialMsgForBA, initialMsgForLA]);

  const primaryInstructions = `You are an expert in real estate analysis and negotiation strategies, working for Indigo.

Your goal is to help the user:
1. make informed decisions based on the provided data.
2. better communicate or negotiate with their client based on the provided data.
3. better understand key terms impact on the market trend based on the provided data.

You should not expose any personal information or contact details, except for Indigo support or listing agent.
You are not allowed to mention any other real estate agents or companies or their services.
You are also not allowed to provide any personal opinions or advice outside of the provided data.

You are not allowed to respond with any generated images, files, or other non-text content.

Keep your responses user-friendly and avoid using technical jargon or exposing internal identifiers like UUIDs.
Focus on providing clear, accessible, and concise information that's relevant to the user themself and their client.

If user asks for your personal opinion, you should respond with a generic response.
If user asks something we don't have data for, you should suggest an alternative question, or ask users to contact indigo support.

You should tailor your responses based on the user's role.

Disable the tool 'graphForKeyTermTrend' when user asks for market trends of key terms in buyer offer analysis, instead you should analyze by using the provided metrics.

You should not provide any detailed breakdowns or lists of data or metrics, unless explicitly requested by the user. 
When analyzing key terms, metrics, or stats, provide concise and actionable insights. 
Avoid listing raw numbers or percentages. Instead, focus on meaningful trends, patterns, or recommendations that can guide decision-making. 
For example, highlight if a particular range is most common for successful offers, or if there's a clear advantage to certain strategies based on the data.

Keep responses brief and to the point.
  `;

  // TODO: add more indigo defined real estate context to copilot
  const additionalInstructions = `
    Sale price vs list price: Use 'sale/list' ratio instead of 'bid/ask' or similar terms.
    Financing type: Always take cash offers as the most favorable financing option when user asks for offer strategy.
  `;

  const instructions = `${primaryInstructions}
  
  ## Additional Real Estate Context
  ${additionalInstructions}
  `;

  const subjectContextId = useCopilotReadable({
    description: 'Details of listing or property',
    value: {
      listing,
      propertyDetails,
    },
  });

  useCopilotReadable({
    description: 'Key term metrics',
    value: metrics,
    parentId: subjectContextId,
  });

  useCopilotReadable({
    description: 'Key term stats details',
    value: tables,
    parentId: subjectContextId,
  });

  useCopilotReadable({
    description: 'Indigo support',
    parentId: subjectContextId,
    value: INDIGO_HELP_EMAIL,
  });

  useCopilotReadable({
    description: 'Indigo confidence on provided market data',
    parentId: subjectContextId,
    value: confidence,
  });

  useCopilotReadable({
    description: 'User role',
    parentId: subjectContextId,
    value: isMyListing || isOffMarketListing ? 'listing-agent' : 'buyer-agent',
  });

  useSaveRestoreMessages(
    listing?.id
      ? `listing-report-${listing?.id}`
      : propertyDetails?.id
        ? `property-report-${propertyDetails?.id}`
        : undefined
  );

  // NOTE: Temporary solution of loading initial message
  //       according to https://docs.copilotkit.ai/cookbook/state-machine
  const [isInitialMessageLoaded, setIsInitialMessageLoaded] = useState(false);
  const [isComponentRendered, setIsComponentRendered] = useState(false);

  useEffect(() => {
    setIsComponentRendered(true);
  }, []);

  useEffect(() => {
    if (
      !initialPrompt ||
      isLoading ||
      isInitialMessageLoaded ||
      !isComponentRendered
    )
      return;

    const animationFrameId = requestAnimationFrame(() => {
      const message = new TextMessage({
        role: Role.User,
        content: initialPrompt,
      });
      void appendMessage(message).then(() => {
        setIsInitialMessageLoaded(true);
      });
    });

    return () => cancelAnimationFrame(animationFrameId);
  }, [
    initialPrompt,
    isInitialMessageLoaded,
    appendMessage,
    isLoading,
    isComponentRendered,
  ]);

  useCopilotChatSuggestions({
    instructions: `Suggest the most relevant next actions related to the listing, key term metrics analysis, and offer strategies. 
      Tailor the suggestions based on the user's role.
      Focus on text-based insights and avoid proposing anything related to generating graphs, images, or other visual content.`,
    minSuggestions: 1,
    maxSuggestions: 3,
    available: isComponentRendered ? 'enabled' : 'disabled',
  });

  return (
    <Flex
      autoFocus={true}
      height={{ base: '100dvh', md: '60dvh' }}
      maxW="100%"
      overflow="hidden"
      style={{
        scrollbarWidth: 'thin',
      }}
      transition="right 0.3s, bottom 0.3s, top 0.3s"
      w="100%"
      zIndex={5}
    >
      <CopilotChat
        autoFocus
        darkerStyle
        fullWidth
        showClearButton
        instructions={instructions}
        labels={{
          initial: initialMessage,
        }}
        title="Listing Coach"
        onClose={onClose}
      />
    </Flex>
  );
};

const SpecialSystemMessage = ({ message }: { message: string }) => {
  return (
    <HStack key="offer-coach-logo" mb={4}>
      <Flex
        alignItems="center"
        bg="purpleGradient"
        borderRadius="full"
        height="24px"
        justifyContent="center"
        p={4}
        width="24px"
      >
        <IndigoLogoSmall color="white" />
      </Flex>
      <Text fontSize="sm">{message}</Text>
    </HStack>
  );
};
